import { Clusterer, Map, Placemark, YMaps } from '@pbe/react-yandex-maps'
import { YMapsApi } from '@pbe/react-yandex-maps/typings/util/typing'
import { useEffect, useMemo, useRef, useState } from 'react'
import { renderToString } from 'react-dom/server'

import type { DeliveryPartnersKeys, DeliveryPointItem } from '@/entities/order/types/delivery'

import { Cluster } from '../../assets/Cluster'
import { getIconLayout } from '../../lib/getIconLayout'

import cl from './shops-map.module.scss'

interface ShopsMapProps {
	points: DeliveryPointItem[]
	onSelect?: (value: DeliveryPointItem) => void
	activePoint?: DeliveryPointItem
	centerCoords: number[]
}

const getClusterIconLayout = () => renderToString(<Cluster content={`{{properties.geoObjects.length}}`} />)

const generateClusterConfig = (yMap: YMapsApi) => ({
	groupByCoordinates: false,
	clusterIconLayout: yMap?.templateLayoutFactory?.createClass(getClusterIconLayout()),
	clusterIconShape: {
		type: 'Rectangle',
		coordinates: [
			[-24, -24],
			[24, 24]
		]
	}
})

const generatePlacemarkConfig = (yMap: YMapsApi, partner: DeliveryPartnersKeys, isActive: boolean) => ({
	iconOffset: !isActive ? [-13, -40] : [-50, -95],
	iconShape: {
		type: 'Rectangle',
		coordinates: [
			[20, 20],
			[50, 50]
		]
	},
	iconLayout: yMap?.templateLayoutFactory?.createClass(getIconLayout(partner, isActive)),
	zIndex: !isActive ? 100 : 2501
})

export const ShopsMap = (props: ShopsMapProps) => {
	const { points, onSelect, activePoint, centerCoords } = props
	const [yMap, setYMap] = useState<YMapsApi>(null)
	const zoomControlRef = useRef<any>(null)
	const clusterConfig = useMemo(() => generateClusterConfig(yMap), [yMap])

	const handleSelect = (point: DeliveryPointItem) => {
		const map = zoomControlRef.current

		const { GPS_N, GPS_S } = point

		map?.setCenter([GPS_N, GPS_S], 17, { duration: 300 })
		onSelect?.(point)
	}

	useEffect(() => {
		if (!activePoint) return
		handleSelect(activePoint)
	}, [activePoint])

	return (
		<div className={cl.root}>
			<YMaps>
				<Map
					instanceRef={zoomControlRef}
					className={cl.map}
					onLoad={yMap => setYMap(yMap)}
					modules={['templateLayoutFactory']}
					state={{
						center: centerCoords,
						zoom: 10
					}}
					defaultState={{
						center: centerCoords,
						zoom: 10
					}}
					defaultOptions={{
						maxAnimationZoomDifference: 100
					}}>
					<Clusterer options={clusterConfig}>
						{points?.map(point => {
							const isActive = point.DELIVERY_PVZ_CODE === activePoint?.DELIVERY_PVZ_CODE

							return (
								<Placemark
									options={generatePlacemarkConfig(yMap, point?.DELIVERY_PVZ_PARTNER, isActive)}
									key={point.DELIVERY_PVZ_CODE}
									geometry={[point.GPS_N, point.GPS_S]}
									onClick={() => handleSelect(point)}
									modules={[`geoObject.addon.hint`]}
								/>
							)
						})}
					</Clusterer>
				</Map>
			</YMaps>
		</div>
	)
}
